import React, { useEffect, useState } from "react";
import TileStep from "../shared/TileStep";
import { ReactComponent as LinkVector } from "../../assets/link-over-ui.svg";
import { ReactComponent as Sandbox } from "../../assets/sandbox.svg";
import { ReactComponent as CircularArrow } from "../../assets/CircularArrow.svg";
import LeftSideBar from "../shared/LeftSideBar";
import LeftSideBarSpacing, { LeftSideBarCol, PaddingLessCol } from "../shared/LeftSideBarSpacing";
import SandboxTile from "./SandboxTile";
import { ReactComponent as BambooHR } from "../../assets/BambooHR.svg";
import { ReactComponent as Workable } from "../../assets/Workable.svg";
import { ReactComponent as Jira } from "../../assets/Jira.svg";
import { ReactComponent as HubSpot } from "../../assets/HubSpot.svg";
import { ReactComponent as QuickBooks } from "../../assets/QuickBooks.svg";
import CustomTileStep from "./CustomTileStep";
import LiveWaitingIndicator from "../shared/LiveWaitingIndicator";
import SyncOverview from "./SyncOverview";
import { LinkedAccount, LinkedAccountStatus } from "../../../../../models/Entities";
import { fetchWithAuth } from "../../../../../api-client/APIClient";
import useDashboardOnboardingStatus from "../../hooks/useDashboardOnboardingStatus";
import { ReactComponent as LinkedAccountSingle } from "../../assets/LinkedAccountsSingle.svg";
import { ReactComponent as SelectOneSingleLine } from "../../assets/longline.svg";
import { Rocket, Wrench } from "lucide-react";
import { ReactComponent as LinkedAccountsSVG } from "../../assets/linked-accounts-svg.svg";
import { Text } from "@merge-api/merge-javascript-shared";
import FeatureCard from "../shared/FeatureCard";

const LinkedAccountSection = () => {
  const dashboardOnboardingStatus = useDashboardOnboardingStatus();
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [linkedAccount, setLinkedAccount] = useState<LinkedAccount | null>(null);
  const [numberOfAccounts, setNumberOfAccounts] = useState<number | undefined>(undefined);

  /*
   *  Pulling all available test Linked Accounts, not including any demo data accounts when it is turned on
   * Sets the value of total Linked Accounts to check if there's a max number of Linked Accounts
   */

  useEffect(() => {
    setLinkedAccounts([]);
    fetchWithAuth({
      path: `/integrations/linked-accounts?status=${LinkedAccountStatus.COMPLETE}&is_test_account=true&page_size=100`,
      method: "GET",
      onResponse: (data) => {
        setLinkedAccounts(data.results || []);
        setNumberOfAccounts(data.results ? data.results.length : 0); // <-- Always set this!
        if (data.results && data.results.length > 0) {
          const firstLinkedAccount = data.results[0];
          setLinkedAccount(firstLinkedAccount);
        }
      },
    });
  }, []);

  return (
    <div className="flex flex-col mx-auto">
      <Text variant="h3" className="mb-6">
        Sync sandbox data
      </Text>
      <div className="flex flex-row">
        <LeftSideBar stepNumber="1" isNormalStep />
        <TileStep
          img={<Sandbox />}
          title="Create sandbox"
          subtitle={
            <>
              <Text as="p">
                Visit one of our recommended third-party platforms below and create a free account.
              </Text>
              <Text as="p">
                This account will have test data for you to play with. This is your{" "}
                <strong>sandbox</strong>.
              </Text>
            </>
          }
          stepNumber={1}
        />
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <PaddingLessCol>
          <div className="mt-10 grid grid-cols-3 gap-x-10 gap-y-10 mb-[92.5px] max-[1195px]:grid-cols-2 max-[1000px]:grid-cols-[minmax(1px,1fr)]">
            <SandboxTile
              integrationImage={<BambooHR />}
              sandboxName="BambooHR"
              categoryName="HRIS"
              sandboxURL="https://www.bamboohr.com/signup/"
            />
            <SandboxTile
              integrationImage={<Workable height={48} width={48} />}
              sandboxName="Workable"
              categoryName="ATS"
              sandboxURL="https://partners.workable.com/merge"
            />
            <SandboxTile
              integrationImage={<Jira />}
              sandboxName="Jira"
              categoryName="Ticketing"
              sandboxURL="https://www.atlassian.com/software/jira/free"
            />
            <SandboxTile
              integrationImage={<HubSpot />}
              sandboxName="HubSpot"
              categoryName="CRM"
              sandboxURL="https://app.hubspot.com/signup-hubspot"
            />
            <SandboxTile
              integrationImage={<QuickBooks />}
              sandboxName="QuickBooks"
              categoryName="Accounting"
              sandboxURL="https://quickbooks.intuit.com/online/advanced/free-trial/"
            />
          </div>
        </PaddingLessCol>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <PaddingLessCol>
          <div />
        </PaddingLessCol>
      </div>
      <div className="flex flex-row">
        <LeftSideBar stepNumber="2" isNormalStep />

        <CustomTileStep
          img={<LinkVector />}
          title="Connect sandbox"
          subtitle={
            <>
              <Text as="p">
                Open <strong>Merge Link</strong> below to connect your sandbox to Merge.
              </Text>
              <Text as="p">
                You'll need to enter <strong>credentials</strong> from your sandbox.
              </Text>
              <Text>
                This connection will be called your <strong>Linked Account</strong>.
              </Text>
            </>
          }
          stepNumber={2}
          numberOfAccounts={numberOfAccounts}
          setNumberOfAccounts={setNumberOfAccounts}
          setLinkedAccounts={setLinkedAccounts}
          setLinkedAccount={setLinkedAccount}
          linkedAccounts={linkedAccounts}
        />
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <div className="flex flex-col items-center justify-center w-full">
          <LinkedAccountsSVG className="relative mt-2 -mb-[10px] max-[1000px]:hidden" />
          &nbsp;
        </div>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <PaddingLessCol>
          <div className="grid grid-cols-1 [grid-template-areas:'container'] max-[1000px]:grid-cols-[1fr_minmax(1px,4fr)] max-[1000px]:[grid-template-areas:'linkedaccounts_...''selectoneline_container']">
            <LinkedAccountSingle className="hidden max-[1000px]:grid max-[1000px]:mb-[10px] [grid-area:linkedaccounts]" />
            <SelectOneSingleLine className="hidden max-[1000px]:grid max-[1000px]:relative max-[1000px]:left-10 [grid-area:selectoneline]" />
            <div className="grid [grid-area:container] grid-cols-2 gap-x-6 gap-y-[22px] justify-evenly max-[1000px]:grid-cols-1">
              <FeatureCard icon={<Wrench size={16} />} title="Test Linked Accounts">
                <Text>
                  <strong>Best used for development of different integrations.</strong>
                </Text>
                <Text>
                  You can <strong>sync data manually</strong> via Merge Dashboard to see our
                  normalized response. This is a great way to test an integration before offering it
                  to your customers.
                </Text>
                <Text>
                  You get <strong>3 free</strong> test Linked Accounts.
                </Text>
              </FeatureCard>

              <FeatureCard icon={<Rocket size={16} />} title="Production Linked Accounts">
                <Text>
                  <strong>Best used as you release your integrations to real users.</strong>
                </Text>

                <Text>
                  Data <strong>syncs automatically</strong>, representing a live connection to your
                  customers' systems!
                </Text>

                <Text>
                  You get <strong>3 free</strong> production Linked Accounts.
                </Text>
              </FeatureCard>
            </div>
          </div>
        </PaddingLessCol>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <PaddingLessCol>
          <div className="mb-[82px]" />
        </PaddingLessCol>
      </div>
      <div className="flex flex-row">
        <LeftSideBar stepNumber="3" isNormalStep={false} />
        <TileStep
          img={
            <div className="relative flex justify-center items-center">
              <CircularArrow className="absolute" />
              <div className="absolute">
                {dashboardOnboardingStatus?.has_successfully_linked_account ? (
                  <LiveWaitingIndicator color="green" />
                ) : (
                  <LiveWaitingIndicator color="red" />
                )}
              </div>
            </div>
          }
          title="Sync sandbox data"
          subtitle={
            <>
              <Text as="p">
                Once you've finished the Merge Link workflow, Merge will automatically start syncing
                data.
              </Text>
              <Text as="p">
                When Merge successfully syncs all data, this step will be finished!
              </Text>
              <Text>
                If you want, update your Sandbox and then resync all to see the changes reflected in
                Merge.
              </Text>
            </>
          }
          stepNumber={3}
        />
      </div>
      <div className="flex flex-row">
        <LeftSideBarCol className="items-center" />
        <SyncOverview
          linkedAccount={linkedAccount}
          linkedAccounts={linkedAccounts}
          setLinkedAccount={setLinkedAccount}
        />
      </div>
    </div>
  );
};

export default LinkedAccountSection;
